import type {
  AssetBlockType,
  LessonAssetDto,
  LessonAssetKind,
  LessonBlock,
  LessonBlockType,
} from '../types';

/**
 * How the block editor uploads a media file. Lesson assets are the default;
 * homework passes its own mirror endpoint (ADR-014 — same kinds, same limits,
 * same response shape).
 */
export type AssetUploader = (
  kind: LessonAssetKind,
  file: File,
  onProgress?: (percent: number) => void
) => Promise<LessonAssetDto>;

export const ASSET_BLOCK_TYPES: readonly AssetBlockType[] = ['image', 'video', 'audio', 'file'];

export function isAssetBlockType(type: LessonBlockType): type is AssetBlockType {
  return (ASSET_BLOCK_TYPES as readonly string[]).includes(type);
}

/**
 * Client-side hint mirroring the backend upload allowlist
 * (curriculum/serializers.py ASSET_ALLOWED_CONTENT_TYPES) — used as the file
 * input's `accept` filter. The server remains the authority; its size/type
 * rejections surface as `field_errors.file`.
 */
export const ASSET_ACCEPT: Record<LessonAssetKind, string> = {
  image: 'image/jpeg,image/png,image/gif,image/webp',
  video: 'video/mp4,video/webm,video/quicktime',
  audio: 'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,audio/webm,audio/x-m4a',
  file: [
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    'application/vnd.oasis.opendocument.text',
    'application/vnd.oasis.opendocument.spreadsheet',
    'application/vnd.oasis.opendocument.presentation',
    'text/plain',
    'text/csv',
    'application/rtf',
    'application/zip',
  ].join(','),
};

/** Fresh editor block of the given type (empty data, unique id). */
export function newBlock(type: LessonBlockType): LessonBlock {
  const id = crypto.randomUUID();
  switch (type) {
    case 'rich_text':
      return { id, type, data: { html: '' } };
    case 'image':
    case 'video':
    case 'audio':
    case 'file':
      return { id, type, data: { asset: '' } };
    case 'youtube':
      return { id, type, data: { video_id: '' } };
    case 'link':
      return { id, type, data: { url: '' } };
    case 'divider':
      return { id, type, data: {} };
  }
}

/**
 * Strip read-time enrichment (url/filename/size/content_type) and empty
 * optional texts before PATCHing — the write payload carries only the block
 * schema fields (curriculum/blocks.py `validate_blocks`).
 */
export function serializeBlocks(blocks: LessonBlock[]): LessonBlock[] {
  return blocks.map((block): LessonBlock => {
    switch (block.type) {
      case 'rich_text':
        return { id: block.id, type: block.type, data: { html: block.data.html } };
      case 'image':
      case 'video':
      case 'audio':
        return {
          id: block.id,
          type: block.type,
          data: {
            asset: block.data.asset,
            ...(block.data.caption ? { caption: block.data.caption } : {}),
          },
        };
      case 'file':
        return {
          id: block.id,
          type: block.type,
          data: {
            asset: block.data.asset,
            ...(block.data.label ? { label: block.data.label } : {}),
          },
        };
      case 'youtube':
        return {
          id: block.id,
          type: block.type,
          data: {
            video_id: block.data.video_id,
            ...(block.data.caption ? { caption: block.data.caption } : {}),
          },
        };
      case 'link':
        return {
          id: block.id,
          type: block.type,
          data: {
            url: block.data.url,
            ...(block.data.label ? { label: block.data.label } : {}),
          },
        };
      case 'divider':
        return { id: block.id, type: block.type, data: {} };
    }
  });
}
